import { z } from "zod";
import {
  estUnControle,
  fichierRenduSchema,
  IMAGE_BASE64_MAX,
  NOM_FICHIER_MAX,
  NOM_MOTIF,
} from "./silhouettes";

/**
 * Contrat du module Cadres de la console (spec du 24 septembre 2026, issue #167) : deposer une
 * image en aplats, reordonner ses couleurs, voir l'empilement des plaques, telecharger un 3MF a
 * un plateau par piece (plaques, cadre, couvercle et dos).
 *
 * La meme requete fait les deux sauts : du navigateur au backend de la console
 * (`POST /api/cadres/apercu` et `POST /api/cadres/fichiers`), puis du backend au service Python
 * `products` (`POST /cadre/apercu` et `POST /cadre/fichiers`). Les reponses reviennent telles
 * que `products` les rend. Rien n'est garde sur fortress.
 *
 * Les noms de champs sont ceux de l'outil (`packages/products/silhouette/silhouette/cadre/`),
 * en francais et en serpent : c'est le contrat de donnees de `cadre/service.py`.
 *
 * Tout ce qui sort par le meme pont que `silhouettes.ts` (`src/app/contract.ts` du backend et
 * du frontend) porte un nom a lui : `REGLAGES_CADRE_PAR_DEFAUT` et non `REGLAGES_PAR_DEFAUT`.
 * Ce qui est commun aux deux modules (l'image en base64, le nom, le nom du fichier, un fichier
 * rendu) vient de `silhouettes.ts`, sans etre recopie.
 */

export const FENETRES = ["rectangle", "ronde"] as const;
export type Fenetre = (typeof FENETRES)[number];

/** Les formats de `fichiers` : le 3MF a un plateau par piece, ou un STL par piece dans un zip. */
export const FORMATS_CADRE = ["3mf", "stl"] as const;
export type FormatCadre = (typeof FORMATS_CADRE)[number];

/** La largeur des plaques demandee. La hauteur suit l'image. */
export const LARGEUR_CADRE_MIN_MM = 80;
export const LARGEUR_CADRE_MAX_MM = 230;

/**
 * La plus grande hauteur de plaque : au-dela, `products` ramene la largeur pour que le cadre,
 * imprime debout, reste sous les 250 mm imprimables de la P1S (spec, section 1).
 */
export const HAUTEUR_CADRE_MAX_MM = 230;

/** L'epaisseur d'une plaque : de 1,2 a 2 mm, par pas de 0,2 mm (la hauteur de couche). */
export const EPAISSEUR_MIN_MM = 1.2;
export const EPAISSEUR_MAX_MM = 2;
export const EPAISSEUR_PAS_MM = 0.2;

/** L'ecart entre deux plaques (la rainure de la boite) : de 2 a 8 mm, par pas de 0,5 mm. */
export const ECART_MIN_MM = 2;
export const ECART_MAX_MM = 8;
export const ECART_PAS_MM = 0.5;
/** L'ecart d'avant la rainure reglable : celui d'une requete qui n'en dit rien. */
export const ECART_PAR_DEFAUT_MM = 4;

/** Un cadre en demande au moins deux : une couleur ne fait qu'une plaque. */
export const COULEURS_CADRE_MIN = 2;
export const COULEURS_CADRE_MAX = 8;

/** Le plus long ordre accepte : deux fois le plus grand nombre de couleurs. */
export const ORDRE_MAX = 16;

/**
 * Le passe-partout du profil livre (`silhouette/profils/cadre.json`, `bordure_mm`). La page ne
 * s'en sert que pour ecrire la hauteur avant le premier apercu : ensuite, elle la deduit des
 * cotes de l'apercu, qui suivent le profil s'il change.
 */
export const PASSE_PARTOUT_MM = 12;

/** Vrai quand `valeur` est un multiple de `pas`, a 1e-6 pres (la tolerance de `products`). */
function multipleDe(valeur: number, pas: number): boolean {
  return Math.abs(valeur - Math.round(valeur / pas) * pas) <= 1e-6;
}

/** Les reglages de la page, bornes comme `cadre/service.py` les borne. */
export const reglagesCadreSchema = z.object({
  nom: z.string().regex(NOM_MOTIF),
  largeur_mm: z.number().min(LARGEUR_CADRE_MIN_MM).max(LARGEUR_CADRE_MAX_MM),
  fenetre: z.enum(FENETRES),
  /** `auto`, ou un nombre impose de 2 a 8. */
  couleurs: z.union([
    z.literal("auto"),
    z.number().int().min(COULEURS_CADRE_MIN).max(COULEURS_CADRE_MAX),
  ]),
  epaisseur_mm: z
    .number()
    .min(EPAISSEUR_MIN_MM)
    .max(EPAISSEUR_MAX_MM)
    .refine((valeur) => multipleDe(valeur, EPAISSEUR_PAS_MM), "multiple de 0,2 mm attendu"),
  /** L'ecart entre deux plaques, de face a face. */
  ecart_mm: z
    .number()
    .min(ECART_MIN_MM)
    .max(ECART_MAX_MM)
    .refine((valeur) => multipleDe(valeur, ECART_PAS_MM), "multiple de 0,5 mm attendu"),
  /** Un fond clair, une cavite et un trou pour la bande LED. */
  led: z.boolean(),
});
export type ReglagesCadre = z.infer<typeof reglagesCadreSchema>;

/** Un nom de couleur de la palette (`couleurs.nommer`, puis `-2` au besoin) : borne, sans controle. */
const nomCouleurSchema = z
  .string()
  .min(1)
  .max(40)
  .refine(
    (nom) => ![...nom].some((car) => estUnControle(car.codePointAt(0) ?? 0)),
    "caractere de controle dans un nom de couleur",
  );

export const apercuRequeteSchema = reglagesCadreSchema.extend({
  /**
   * Une requete sans `ecart_mm` ni `led` reste acceptee (4 mm, sans LED) : un onglet ouvert avant
   * le deploiement du cadre a rainures ne les envoie pas, et recevrait un 400 au lieu d'un
   * apercu. Les reponses, elles, restent strictes.
   */
  ecart_mm: reglagesCadreSchema.shape.ecart_mm.default(ECART_PAR_DEFAUT_MM),
  led: reglagesCadreSchema.shape.led.default(false),
  /** Les octets PNG, JPEG ou WebP en base64, sans prefixe `data:`. */
  image: z.base64().min(1).max(IMAGE_BASE64_MAX),
  /** Le nom du fichier depose, pour le rapport : 200 unites au plus, aucun caractere de controle. */
  nom_fichier: z
    .string()
    .max(NOM_FICHIER_MAX)
    .refine(
      (nom) => ![...nom].some((car) => estUnControle(car.codePointAt(0) ?? 0)),
      "caractere de controle dans le nom du fichier",
    )
    .optional(),
  /**
   * `null` : l'ordre propose par l'outil. Sinon chaque couleur de la palette une fois, de
   * l'avant vers le fond ; c'est `products` qui verifie qu'il nomme bien la palette (400 sinon,
   * avec la palette dans le message).
   */
  ordre: z
    .array(nomCouleurSchema)
    .min(1)
    .max(ORDRE_MAX)
    .refine((noms) => new Set(noms).size === noms.length, "une couleur en double dans l'ordre")
    .nullable(),
});
export type ApercuRequete = z.infer<typeof apercuRequeteSchema>;

export const fichiersRequeteSchema = apercuRequeteSchema.extend({
  format: z.enum(FORMATS_CADRE).default("3mf"),
});
/** Ce que la page envoie : `format` peut manquer, il vaut alors `3mf`. */
export type FichiersRequete = z.input<typeof fichiersRequeteSchema>;

/** `#16161a` : la seule forme que `products` ecrit, et la seule que la page pose en style. */
const hexaSchema = z.string().regex(/^#[0-9a-fA-F]{6}$/);

/** Une couleur de la palette, de la plus etendue a la moins etendue. */
export const couleurPaletteSchema = z.object({
  nom: z.string(),
  hexa: hexaSchema,
  /** La part de la fenetre qu'elle couvre, en %. */
  surface_pct: z.number(),
});
export type CouleurPalette = z.infer<typeof couleurPaletteSchema>;

/** Une plaque de l'apercu, de l'avant (rang 1) vers le fond. */
export const plaqueApercuSchema = z.object({
  rang: z.number().int().min(1),
  couleur: z.string(),
  hexa: hexaSchema,
  /** Vrai pour une plaque qui porte ce qu'une couleur n'a pas pu poser plus tot (« noir, au fond »). */
  seconde_passe: z.boolean(),
  /** La part de la fenetre ou cette plaque se voit de face, en %. */
  visible_pct: z.number(),
  grammes: z.number(),
  parties_fines: z.number().int().min(0),
  /** La plaque vue de face, couleur pleine et transparente dans ses trous, 800 px de large. */
  png: z.base64(),
  /** Le calque de ses parties fines, en rose sur fond transparent ; `null` quand il n'y en a pas. */
  fines_png: z.base64().nullable(),
});
export type PlaqueApercu = z.infer<typeof plaqueApercuSchema>;

/** Une partie de couleur entouree par d'autres, reportee au fond sur une plaque de seconde passe. */
export const reporteeSchema = z.object({
  couleur: z.string(),
  surface_pct: z.number(),
  /** Les couleurs qui l'entourent. */
  entouree_par: z.array(z.string()),
  /** Le rang de la plaque de seconde passe qui la porte. */
  plaque: z.number().int().min(1),
});
export type Reportee = z.infer<typeof reporteeSchema>;

/**
 * Le rapport de l'outil (`asdict(RapportCadre)`), sans aucun chemin du serveur. Seuls les
 * champs que la console lit sont decrits ; les autres passent tels quels (`loose`), pour qu'un
 * champ ajoute a l'outil n'arrete pas la page. Les grammes du cadre et du dos peuvent manquer :
 * l'apercu ne fabrique aucun maillage.
 */
export const rapportCadreSchema = z
  .object({
    /** Le nombre de plaques de l'ordre applique. */
    plaques: z.number().int().min(1),
    /** La palette, comme `palette` dans l'apercu. */
    couleurs: z.array(couleurPaletteSchema),
    /** L'epaisseur des plaques appliquee : la perspective recule chaque plaque d'autant. */
    epaisseur_mm: z.number().positive().optional(),
    /**
     * La largeur de la requete, et vrai quand l'outil l'a ramenee pour que la hauteur tienne
     * dans 230 mm. C'est d'eux que la page tire « image en portrait », et non de la largeur
     * tapee, qui peut deja en demander une autre (relecture de #176).
     */
    largeur_demandee_mm: z.number().positive().optional(),
    largeur_reduite: z.boolean().optional(),
    /** Le compte des parties fines de toute la piece (une par endroit, pas par plaque). */
    parties_fines: z.number().int().min(0).optional(),
    grammes_cadre: z.number().nullable().optional(),
    grammes_dos: z.number().nullable().optional(),
    /** L'ecart entre plaques applique, et si le cadre est prevu pour une LED. */
    ecart_mm: z.number().optional(),
    led: z.boolean().optional(),
    /** L'epaisseur du fond (la derniere plaque). */
    fond_mm: z.number().optional(),
    grammes_couvercle: z.number().nullable().optional(),
    /** Les messages de l'outil, en ASCII : la page les traduit (`cadres-textes.ts`). */
    avertissements: z.array(z.string()),
    /** Des noms de fichiers seuls, jamais un chemin. */
    fichiers: z.array(z.string()).optional(),
  })
  .loose();
export type RapportCadre = z.infer<typeof rapportCadreSchema>;

/** Deux cotes en millimetres, largeur puis hauteur. */
const cotesSchema = z.tuple([z.number(), z.number()]);

export const apercuReponseSchema = z.object({
  /** La palette, de la couleur la plus etendue a la moins etendue. */
  palette: z.array(couleurPaletteSchema).min(1),
  /** L'ordre que l'outil propose, de l'avant vers le fond. */
  ordre_propose: z.array(z.string()),
  /** Le nombre de plaques de l'ordre propose : le compteur dit ce que coute un autre ordre. */
  plaques_ordre_propose: z.number().int().min(1),
  /** L'ordre effectif, celui des premieres plaques de chaque couleur : c'est lui que la page montre. */
  ordre: z.array(z.string()),
  plaques: z.array(plaqueApercuSchema).min(1),
  /** Les parties reportees au fond par l'ordre applique. */
  reportees: z.array(reporteeSchema),
  /** L'anneau du cadre vu de face, transparent au centre, a l'echelle des plaques. */
  cadre_png: z.base64(),
  /** La planche : la vue de face puis une vignette par plaque, parties fines en rose. */
  planche_png: z.base64(),
  dimensions: z.object({
    plaques_mm: cotesSchema,
    fenetre_mm: cotesSchema,
    /** Le cadre monte : largeur, hauteur, profondeur. */
    hors_tout_mm: z.tuple([z.number(), z.number(), z.number()]),
    /** L'ecart entre deux plaques, le pas d'une plaque a la suivante et l'epaisseur du fond. */
    ecart_mm: z.number(),
    pas_mm: z.number(),
    fond_mm: z.number(),
  }),
  rapport: rapportCadreSchema,
  /** Le rapport en texte, tel que l'outil l'ecrit. */
  texte: z.string(),
  /** Vrai quand `products` a du reduire une image de plus de 2 500 px de cote. */
  image_reduite: z.boolean(),
});
export type ApercuReponse = z.infer<typeof apercuReponseSchema>;

export const fichiersReponseSchema = z.object({
  rapport: rapportCadreSchema,
  texte: z.string(),
  /** `<nom>.3mf`, ou `<nom>-stl.zip` avec `format: "stl"`. */
  fichiers: z.array(fichierRenduSchema).min(1),
  image_reduite: z.boolean(),
});
export type FichiersReponse = z.infer<typeof fichiersReponseSchema>;

/** Ce que la page lit a l'ouverture : les reglages par defaut, leurs bornes et celles de l'image. */
export const cadresInfoSchema = z.object({
  reglages: reglagesCadreSchema,
  bornes: z.object({
    largeur_min_mm: z.number(),
    largeur_max_mm: z.number(),
    hauteur_max_mm: z.number(),
    epaisseur_min_mm: z.number(),
    epaisseur_max_mm: z.number(),
    epaisseur_pas_mm: z.number(),
    ecart_min_mm: z.number(),
    ecart_max_mm: z.number(),
    ecart_pas_mm: z.number(),
    couleurs_min: z.number(),
    couleurs_max: z.number(),
  }),
  limites: z.object({
    image_source_octets_max: z.number(),
    image_envoi_octets_max: z.number(),
    cote_envoi_px: z.number(),
    cote_envoi_opaque_px: z.number(),
    calcul_delai_s: z.number(),
  }),
});
export type CadresInfo = z.infer<typeof cadresInfoSchema>;

/** 200 mm de large, fenetre rectangulaire, couleurs trouvees par l'outil, plaques de 1,6 mm a 4 mm d'ecart, sans LED. */
export const REGLAGES_CADRE_PAR_DEFAUT: ReglagesCadre = {
  nom: "cadre",
  largeur_mm: 200,
  fenetre: "rectangle",
  couleurs: "auto",
  epaisseur_mm: 1.6,
  ecart_mm: ECART_PAR_DEFAUT_MM,
  led: false,
};
